"use client";
import React from "react";
import { renderIcon } from "@/lib/icon";
import type { HeroProps } from "@/types/sections/hero";
import { TextColorClassOrStyle } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
  getBackgroundStyle,
  getBtnVariant,
  paddingMap,
} from "@/lib/background";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";

interface Props {
  data: HeroProps;
}

export default function HeroSplit({ data }: Props) {
  const {
    title,
    subtitle,
    features = [],
    buttons = [],
    background,
    padding = "xl",
  } = data || {};

  const bgStyle = getBackgroundStyle(background);

  // Color for title & subtitle
  const tcl = TextColorClassOrStyle(data.colors?.title, "text-foreground");
  const scl = TextColorClassOrStyle(
    data.colors?.subtitle,
    "text-muted-foreground",
  );
  return (
    <section
      className={`${paddingMap[padding] || "py-20 px-6"} relative`}
      style={bgStyle}
    >
      {background?.type === "image" && background?.value && (
        <Image
          src={background.value}
          alt={title || subtitle || ""}
          fill
          priority
          className="object-cover object-center z-0"
        />
      )}
      <div className="max-w-5xl mx-auto text-center relative z-10">
        {title && (
          <h1
            className={cn("text-4xl sm:text-5xl font-bold mb-4", tcl.className)}
            style={tcl.style}
          >
            {title}
          </h1>
        )}
        {subtitle && (
          <p className={cn("text-lg mb-6", scl.className)} style={scl.style}>
            {subtitle}
          </p>
        )}
        {buttons.length > 0 && (
          <div className="flex justify-center gap-4 flex-wrap mb-8">
            {buttons.map((btn, idx) => (
              <Link key={idx} href={btn.href || "#"}>
                <Button variant={getBtnVariant(btn.style)}>{btn.text}</Button>
              </Link>
            ))}
          </div>
        )}
        {features.length > 0 && (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
            {features.map((f, i) => (
              <div
                key={i}
                className="p-4 border rounded-lg bg-white shadow text-left"
              >
                <div className="text-2xl mb-2">
                  {renderIcon({ icon: f.icon, size: 24 })}
                </div>
                <p className="text-sm">{f.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
